import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ReactComponent as PlusCircle } from "@/assets/images/plus-background.svg";
import { ReactComponent as MinusCircle } from "@/assets/images/minus-background.svg";
import EstimateBadge from "../components/EstimateBadge";
import { ReactComponent as PencilSvg } from "@/assets/images/pencil-white.svg";
import { ChevronDown } from "lucide-react";
import { CombinedRow, ItemSheetData } from "@/types/itemsheet";
import EditableCell from "../components/table/EstimateEditableCell";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import React from "react";
import SearchIcon from "@/assets/images/search-line.png";
import CommonImg from "@/components/Common/Img";
import ChangeTppProductAlert from "../components/ChangeTppProductAlert";

export const createColumns = (
  isEditing: boolean,
  isExcelDownloading: boolean,
  expandedRowIds: { [key: string]: boolean },
  setExpandedRowId: (rowId: string, isExpanded: boolean) => void,
  handleDelete: (uuid: string) => void,
  handleAddExtraColumn: (key: string, defaultValue?: any) => void,
  generateNextExtraKey: () => string,
  setExtraKeyLabels: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >,
  openDialog: (rowIndex: number, productId: string) => void,
  handleApplyTppProduct: (rowIndex: number) => void,
  originalRows: CombinedRow[] = []
): ColumnDef<CombinedRow>[] => {
  // 원본 데이터에서 특정 행의 특정 필드 값을 가져오는 헬퍼 함수
  const getOriginalValue = (
    uuid: string,
    accessorKey: keyof CombinedRow
  ): string | number | null | undefined => {
    const originalRow = originalRows.find((row) => row.uuid === uuid);
    if (!originalRow) return undefined;

    const value = originalRow[accessorKey];

    // EditableCell에서 허용하는 타입만 반환
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      value === null ||
      value === undefined
    ) {
      return value;
    }

    // 다른 타입의 경우 undefined 반환
    return undefined;
  };

  return [
    {
      id: "select",
      header: ({ table }) =>
        isExcelDownloading || isEditing ? (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
            className="flex size-4 border-gray-A data-[state=checked]:bg-green data-[state=checked]:text-white"
          />
        ) : (
          <></>
        ),
      cell: ({ row, column }) => {
        return isExcelDownloading || isEditing ? (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="flex size-4 border-gray-B data-[state=checked]:bg-green data-[state=checked]:text-white data-[state=checked]:border-transparent"
          />
        ) : (
          <span className="flex justify-center">{row.original.seq_no}</span>
        );
      },
      size: 40,
    },
    {
      accessorKey: "product_name",
      header: "제품명",
      cell: ({ row, column }) => {
        const {
          product_name,
          tags = [],
          googleSearchResult,
          purchaseHistory,
          list_price,
        } = row.original;

        const isExpanded = expandedRowIds[row.id];
        const isAlternativeSelectPossible =
          googleSearchResult?.length > 0 || purchaseHistory?.length > 0;
        const isOwnProduct = tags.some(
          (tag) => tag === ItemSheetExtraTag.OWN_PRODUCT
        );

        const handleToggleExpand = () => setExpandedRowId(row.id, !isExpanded);

        return (
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 flex items-center gap-2">
              <EditableCell
                isEditing={isEditing}
                value={product_name || ""}
                rowIndex={row.index}
                accessorKey={column.id as keyof CombinedRow}
                originalValue={getOriginalValue(
                  row.original.uuid!,
                  column.id as keyof CombinedRow
                )}
              />
              {tags.map((tag) => {
                return (
                  <EstimateBadge
                    type={tag}
                    key={`${product_name}-${tag}`}
                    noLabel
                  />
                );
              })}
            </div>
            {isEditing &&
              isAlternativeSelectPossible &&
              (isExpanded ? (
                <div
                  className="flex items-center gap-[2px] px-[6px] bg-dark-brown rounded-sm cursor-pointer shadow-sm text-white"
                  onClick={handleToggleExpand}
                >
                  <span className="text-[8px] font-semibold">
                    적용할 데이터 선택
                  </span>
                  <ChevronDown className="size-2" />
                </div>
              ) : (
                isOwnProduct || (
                  <div
                    className="size-4 flex items-center justify-center rounded-sm bg-dark-brown cursor-pointer "
                    onClick={handleToggleExpand}
                  >
                    <PencilSvg />
                  </div>
                )
              ))}
          </div>
        );
      },
      size: 450,
    },
    {
      accessorKey: "product_id",
      header: "제품번호",
      cell: ({ row, column }) => {
        if (!isEditing) {
          const productId = row.getValue(column.id) as string;
          const tags = row.original.tags || [];
          const isOwnProduct = tags.some(
            (tag) => tag === ItemSheetExtraTag.OWN_PRODUCT
          );

          return (
            <div className="relative group w-full">
              <div className="flex items-center justify-between">
                <span className="mr-10 text-xs text-gray-A font-normal">
                  {productId}
                </span>

                <ChangeTppProductAlert
                  onApply={() => handleApplyTppProduct(row.index)}
                  rowIndex={row.index}
                  isOwnProduct={isOwnProduct}
                />
                <CommonImg
                  src={SearchIcon}
                  alt="S"
                  className="absolute top-1/2 transform -translate-y-1/2 right-0 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => openDialog(row.index, productId)}
                />
              </div>
            </div>
          );
        }

        return (
          <EditableCell
            isEditing={isEditing}
            value={row.getValue(column.id)}
            rowIndex={row.index}
            accessorKey={column.id as keyof CombinedRow}
            originalValue={getOriginalValue(
              row.original.uuid!,
              column.id as keyof CombinedRow
            )}
          />
        );
      },
      size: 175,
    },
    {
      accessorKey: "manufacturer",
      header: "제조사",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 220,
    },
    {
      accessorKey: "vendor",
      header: "벤더",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 175,
    },
    {
      accessorKey: "buyer",
      header: "구매업체",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 175,
    },
    {
      accessorKey: "specification",
      header: "규격",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 110,
    },
    {
      accessorKey: "unit",
      header: "단위",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 100,
    },
    {
      accessorKey: "quantity",
      header: "수량",
      cell: ({ row, column }) => (
        <EditableCell
          isEditing={isEditing}
          value={row.getValue(column.id)}
          rowIndex={row.index}
          accessorKey={column.id as keyof CombinedRow}
          originalValue={getOriginalValue(
            row.original.uuid!,
            column.id as keyof CombinedRow
          )}
        />
      ),
      size: 100,
    },
    {
      accessorKey: "unit_cost",
      header: "매입가",
      cell: ({ row, column }) => {
        const { tags } = row.original;
        const isPastPurchase = tags.some(
          (tag) => tag === ItemSheetExtraTag.PAST_PURCHASE
        );

        return (
          <EditableCell
            isEditing={isEditing}
            value={row.getValue(column.id) as number}
            rowIndex={row.index}
            accessorKey={column.id as keyof CombinedRow}
            originalValue={getOriginalValue(
              row.original.uuid!,
              column.id as keyof CombinedRow
            )}
          />
        );
      },
      size: 150,
    },
    {
      accessorKey: "list_price",
      header: "판매가",
      cell: ({ row, column }) => {
        const { unit_cost, tags } = row.original;
        const isPastPurchase = tags.some(
          (tag) => tag === ItemSheetExtraTag.PAST_PURCHASE
        );

        return (
          <EditableCell
            isEditing={isEditing}
            value={row.getValue(column.id) as number}
            rowIndex={row.index}
            accessorKey={column.id as keyof CombinedRow}
            originalValue={getOriginalValue(
              row.original.uuid!,
              column.id as keyof CombinedRow
            )}
          />
        );
      },
      size: 150,
    },
    {
      id: "total_cost",
      header: "매입가 합계",
      accessorFn: (row: CombinedRow) => {
        // 편집 모드가 아닐 때)서버에서 받은 total_cost 값 사용
        if (!isEditing && row.total_cost != null) {
          return typeof row.total_cost === "string"
            ? parseFloat(row.total_cost)
            : row.total_cost;
        }

        // 편집 모드일 때) 프론트에서 계산
        if (row.quantity != null && row.unit_cost != null) {
          const unitCost =
            typeof row.unit_cost === "string"
              ? parseFloat(row.unit_cost)
              : row.unit_cost;
          return row.quantity * unitCost;
        }

        return null;
      },
      cell: ({ getValue }) => {
        const total = getValue() as number | null;
        return (
          <span className="text-xs text-gray-A font-semibold">
            {total != null ? total.toLocaleString() : ""}
          </span>
        );
      },
      size: 150,
    },
    {
      id: "total_list_price",
      header: "판매가 합계",
      accessorFn: (row: CombinedRow) => {
        // 편집 모드가 아닐 때는 서버에서 받은 total_list_price 값을 그대로 사용
        if (!isEditing && row.total_list_price != null) {
          return typeof row.total_list_price === "string"
            ? parseFloat(row.total_list_price)
            : row.total_list_price;
        }

        // 편집 모드일 때는 프론트에서 계산
        if (row.quantity != null && row.list_price != null) {
          const listPrice =
            typeof row.list_price === "string"
              ? parseFloat(row.list_price)
              : row.list_price;
          return row.quantity * listPrice;
        }

        return null;
      },
      cell: ({ getValue }) => {
        const total = getValue() as number | null;
        return (
          <span className="text-xs text-gray-A font-semibold">
            {total != null ? total.toLocaleString() : ""}
          </span>
        );
      },
      size: 150,
    },
    {
      id: "actions",
      header: () =>
        isEditing ? (
          <PlusCircle
            className="size-4 cursor-pointer"
            onClick={() => {
              const newKey = generateNextExtraKey();
              handleAddExtraColumn(newKey, "");
              setExtraKeyLabels((prev) => ({
                ...prev,
                [newKey]: "",
              }));
            }}
          />
        ) : null,
      cell: ({ row }) =>
        isEditing ? (
          <MinusCircle
            className="size-4 cursor-pointer"
            onClick={() => handleDelete(row.original.uuid!)}
          />
        ) : null,
      size: 50,
    },
  ];
};
